import { addDays, format, isPast, isToday } from 'date-fns'

import { SCHEDULED_ORDERS as dict } from '@/resources/dict'
import { DeliveryType, deliveryTypeEnum } from '@/resources/enums/deliveryTypes'
import orderStatusEnum, { OrderStatus } from '@/resources/enums/orderStatus'
import { getCookie, addTimeZone, parseUTCDate } from '@/resources/utils'
import { isCbe } from '@/resources/utils/order'
import { ActiveOrder, DeliveryDate } from '@/resources/types'

import helpCircle from '@/assets/imgs/help-circle-blue.svg'
import { ptBR } from 'date-fns/locale'

// * Constants

const REPORT_BUTTON_ACTIVE_PERIOD = {
  OPEN: 0,
  CLOSE: 10,
}

const SCHEDULED_ORDER_READY_TIME = 10 * 60 * 1000

export const SCHEDULED_ORDERS_REPORT_WARNING_COOKIE =
  'scheduled_orders_report_warning'

export const SCHEDULED_ORDERS_DELIVERY_WARNING_COOKIE =
  'scheduled_orders_delivery_warning'

export const PRINT_HEIGHT_VALUES = {
  PAPER: 1005,
  CHECKLIST_COMPONENT: 149,
  CHECKLIST_COMPONENT_ITEM: 26,
}

export const SCHEDULED_ORDERS_FILTER_TYPES = {
  CUSTOMER_NAME: 'customerName',
  ORDER_NUMBER: 'orderNumber',
  ORDER_NEIGHBORHOOD: 'orderNeighborhood',
}

const SCHEDULED_ORDERS_DELIVERY_WARNING_HOURS = [9, 11, 13, 15, 17, 19, 21]

const SCHEDULED_ORDERS_MODAL_LOCAL_STORAGE_KEY =
  'SCHEDULED_ORDERS_MODAL_ALREADY_SEEN'

// * Helpers()

export const getToday = () => new Date().toLocaleDateString('pt-BR')

export const getReportButtonActive = ({
  currentHour,
}: {
  currentHour: number
}) =>
  currentHour >= REPORT_BUTTON_ACTIVE_PERIOD.OPEN &&
  currentHour < REPORT_BUTTON_ACTIVE_PERIOD.CLOSE

export const getScheduledOrderReady = ({
  startDeliveryDate,
}: {
  startDeliveryDate: number
}) => {
  const today = new Date()
  const deliveryTimeRemaining = startDeliveryDate - today.getTime()

  return deliveryTimeRemaining <= SCHEDULED_ORDER_READY_TIME
}

export const getTodayScheduledOrders = ({
  startDeliveryDate,
}: {
  startDeliveryDate: string
}) => {
  return startDeliveryDate === getToday()
}

export const getDefaultDeliveryDate = ({ orderDate }: { orderDate: Date }) => {
  const defaultStartDeliveryDate = addDays(orderDate, 1)
  defaultStartDeliveryDate.setHours(10, 0, 0, 0)

  const defaultEndDeliveryDate = addDays(orderDate, 1)
  defaultEndDeliveryDate.setHours(18, 0, 0, 0)

  return { defaultStartDeliveryDate, defaultEndDeliveryDate }
}

export const getScheduledOrders = ({
  orders = [],
}: {
  orders: Array<ActiveOrder>
}) => {
  const filteredOrders = orders.filter(order => {
    if (order.delivery?.type !== DeliveryType.SCHEDULED) {
      return false
    }

    const orderStatus = order.status
    const orderDate = addTimeZone(order.date)

    if (
      !order.delivery?.deliveryDate?.startDate ||
      !order.delivery?.deliveryDate?.endDate
    ) {
      const { defaultStartDeliveryDate, defaultEndDeliveryDate } =
        getDefaultDeliveryDate({
          orderDate,
        })

      order.delivery.deliveryDate = {
        startDate: defaultStartDeliveryDate.toISOString(),
        endDate: defaultEndDeliveryDate.toISOString(),
      }
    }

    const orderStartDeliveryDate = addTimeZone(
      order.delivery?.deliveryDate?.startDate
    )

    const isAccepted = orderStatus === orderStatusEnum.POC_ACCEPTED
    const isScheduledOrderReady = getScheduledOrderReady({
      startDeliveryDate: orderStartDeliveryDate.getTime(),
    })
    const isExpiredDeliveryDate = isPast(orderStartDeliveryDate)

    return isAccepted && !isScheduledOrderReady && !isExpiredDeliveryDate
  })

  return filteredOrders
}

export const getScheduledOrdersLengthForSidebar = (orders?: ActiveOrder[]) => {
  if (!orders?.length) return 0
  return getScheduledOrders({ orders }).length
}

export const getScheduledOrdersKanban = ({
  orders = [],
  includedAllTodayOrders = false,
  includedStatus = undefined,
  includeChoppOrders = false,
  includeNewOrders = false,
}: {
  orders: Array<ActiveOrder>
  includedAllTodayOrders?: boolean
  includedStatus?: string[]
  includeChoppOrders?: boolean
  includeNewOrders?: boolean
}) => {
  const filteredOrders = orders
    .filter(order => {
      const orderDeliveryType = order.delivery?.type
      const isScheduled = orderDeliveryType === deliveryTypeEnum.SCHEDULED
      const isStatusIncluded =
        !includedStatus || includedStatus.includes(order.status)
      const isChopp = includeChoppOrders && isCbe(order.checkoutType)
      const isNewOrder =
        includeNewOrders &&
        isNewScheduledOrder(order.status as OrderStatus, orderDeliveryType)

      if (isChopp || isNewOrder) return true
      if (!isScheduled || !isStatusIncluded) return false

      const orderDate = addTimeZone(order.date)
      const deliveryStartDate = order.delivery?.deliveryDate?.startDate
      const deliveryEndDate = order.delivery?.deliveryDate?.endDate

      if (!deliveryStartDate || !deliveryEndDate) {
        const { defaultStartDeliveryDate, defaultEndDeliveryDate } =
          getDefaultDeliveryDate({
            orderDate,
          })

        order.delivery.deliveryDate = {
          startDate: defaultStartDeliveryDate.toISOString(),
          endDate: defaultEndDeliveryDate.toISOString(),
        }
      }

      const orderStartDeliveryDate = addTimeZone(deliveryStartDate)

      const isScheduledOrderReady = includedAllTodayOrders
        ? getTodayScheduledOrders({
            startDeliveryDate:
              orderStartDeliveryDate.toLocaleDateString('pt-BR'),
          })
        : getScheduledOrderReady({
            startDeliveryDate: orderStartDeliveryDate.getTime(),
          })

      return isScheduledOrderReady
    })
    .sort((orderA, orderB) => {
      const deliveryStartDateA = addTimeZone(
        orderA?.delivery?.deliveryDate?.startDate
      )
      const deliveryStartDateB = addTimeZone(
        orderB?.delivery?.deliveryDate?.startDate
      )

      const deliveryStartDateAms = deliveryStartDateA?.getTime()
      const deliveryStartDateBms = deliveryStartDateB?.getTime()

      return deliveryStartDateAms - deliveryStartDateBms
    })
  return filteredOrders
}

const getScheduledOrdersModalStorageKey = (pocId: string | number) =>
  `${SCHEDULED_ORDERS_MODAL_LOCAL_STORAGE_KEY}_${pocId}`

export const hasModalAlreadyShow = (pocId: string | number) => {
  const key = getScheduledOrdersModalStorageKey(pocId)
  const lastState = localStorage.getItem(key)

  if (lastState === getToday()) {
    return true
  }
  localStorage.removeItem(key)
  return false
}

export const setModalAlreadyShow = (pocId: string | number) => {
  localStorage.setItem(getScheduledOrdersModalStorageKey(pocId), getToday())
}

export const shouldRenderModal = () => {
  const now = new Date()

  const currentHours = now.getHours()
  const currentMinutes = now.getMinutes()

  const specificHours = 9
  const specificMinutes = 50
  return (
    currentHours > specificHours ||
    (currentHours === specificHours && currentMinutes > specificMinutes)
  )
}

export const getScheduledOrdersReportWarningActive = ({
  currentDay,
}: {
  currentDay: number
}) => {
  const scheduledOrderWarningDayClick = parseInt(
    getCookie(SCHEDULED_ORDERS_REPORT_WARNING_COOKIE) || ''
  )

  if (currentDay === scheduledOrderWarningDayClick) {
    return false
  }

  return true
}

export const getScheduledOrdersDeliveryWarningActive = ({
  currentHour,
}: {
  currentHour: number
}) => {
  const scheduledOrderWarningHourClick = parseInt(
    getCookie(SCHEDULED_ORDERS_DELIVERY_WARNING_COOKIE) || ''
  )

  if (
    !SCHEDULED_ORDERS_DELIVERY_WARNING_HOURS.includes(currentHour) ||
    currentHour === scheduledOrderWarningHourClick
  ) {
    return false
  }

  return true
}

export const renderPrintContainer = () => {
  const printContainerCreated = document.getElementById(
    'scheduled-orders-print-container'
  )

  if (printContainerCreated) {
    return printContainerCreated
  }

  const printContainer = document.createElement('div')
  printContainer.setAttribute('id', 'scheduled-orders-print-container')
  document.body.prepend(printContainer)

  return printContainer
}

export const getGuideLink = () => ({
  text: dict.guideLabel,
  to: 'https://www.figma.com/proto/v4v5VNHvtFlNbDkkAvCtVW/Compra-planejada?node-id=3569-1941&t=L1YHrgjrawfocIVw-1&scaling=min-zoom&content-scaling=fixed&page-id=3569%3A1906',
  id: 'order-scheduled-order-link',
  icon: helpCircle,
})

export const getShouldBreakPage = ({
  childrenQuantity,
  printHeightRemaining,
}: {
  childrenQuantity: number
  printHeightRemaining: number
}) => {
  const {
    CHECKLIST_COMPONENT: componentHeight,
    CHECKLIST_COMPONENT_ITEM: componentChildrenHeight,
  } = PRINT_HEIGHT_VALUES

  const requiredHeight =
    componentHeight + componentChildrenHeight * childrenQuantity

  const remainingHeight = printHeightRemaining - requiredHeight

  const shouldBreakPage = remainingHeight < 0

  return { shouldBreakPage, remainingHeight }
}

export const getScheduledOrdersFilterTypes = (): {
  label: string
  value: string
}[] => [
  {
    label: dict.filter.types.customerName,
    value: SCHEDULED_ORDERS_FILTER_TYPES.CUSTOMER_NAME,
  },
  {
    label: dict.filter.types.orderNumber,
    value: SCHEDULED_ORDERS_FILTER_TYPES.ORDER_NUMBER,
  },
  {
    label: dict.filter.types.orderNeighborhood,
    value: SCHEDULED_ORDERS_FILTER_TYPES.ORDER_NEIGHBORHOOD,
  },
]

export const getReportOrders = ({ orders }: { orders: Array<ActiveOrder> }) => {
  const reportOrders = orders.filter(order => {
    const orderDeliveryDate = order.delivery?.deliveryDate?.startDate

    return isToday(addTimeZone(orderDeliveryDate))
  })

  return reportOrders
}

export type FormattedWindowTimeType = {
  startHour: string | Date | null
  endHour: string | Date | null
}

export interface DeliveryDateFormat {
  formattedDeliveryDay: string | Date | null
  formattedWindowTime: FormattedWindowTimeType
}

export const getDeliveryDateFormatted = ({
  deliveryDate,
  deliveryDayFormat = 'dd/MM/yyyy',
  hourFormat = 'HH:mm',
}: {
  deliveryDate?: DeliveryDate | null
  deliveryDayFormat?: string
  hourFormat?: string
}): DeliveryDateFormat => {
  if (!deliveryDate) {
    return {
      formattedDeliveryDay: '',
      formattedWindowTime: { startHour: '', endHour: '' },
    }
  }

  const deliveryStartDate = deliveryDate?.startDate
  const deliveryEndDate = deliveryDate?.endDate

  const formattedDeliveryDay = deliveryStartDate
    ? parseUTCDate(addTimeZone(deliveryStartDate), deliveryDayFormat)
    : null

  const formattedWindowTime = {
    startHour: deliveryStartDate
      ? parseUTCDate(deliveryStartDate, hourFormat)
      : null,
    endHour: deliveryEndDate ? parseUTCDate(deliveryEndDate, hourFormat) : null,
  }

  return {
    formattedDeliveryDay,
    formattedWindowTime,
  }
}

export const getFullFormattedDeliveryDate = (
  deliveryDate?: DeliveryDate | null
) => {
  if (!deliveryDate || !deliveryDate.startDate || !deliveryDate.endDate)
    return null

  return {
    weekday: format(deliveryDate.startDate, 'eeee', {
      locale: ptBR,
    }),
    day: format(deliveryDate.startDate, '(dd/MM)'),
    startTime: format(deliveryDate.startDate, 'HH:mm'),
    endTime: format(deliveryDate.endDate, 'HH:mm'),
  }
}

const isNewScheduledOrder = (status: OrderStatus, type: DeliveryType) =>
  type === DeliveryType.SCHEDULED &&
  (status === OrderStatus.PENDING || status === OrderStatus.TRANSFERED)
